'use client'

import type { JSX } from 'react'

import useRevealOnView from './use-reveal-on-view'

// The draw-in for each line, and the fade-in for each dot and label, both gated on `reveal:`.
const DRAW =
  'reveal:[stroke-dasharray:900] reveal:[stroke-dashoffset:900] reveal:transition-[stroke-dashoffset] reveal:duration-[3000ms] reveal:ease-[ease] reveal:[[data-visible]_&]:[stroke-dashoffset:0]'
const FADE =
  'reveal:opacity-0 reveal:transition-opacity reveal:duration-[160ms] reveal:ease-[ease] reveal:delay-[2840ms] reveal:[[data-visible]_&]:opacity-100'

// The axis labels: x, y, anchor, text.
const AXIS: [number, number, 'start' | 'middle' | 'end', string][] = [
  [52, 198, 'end', '30M'],
  [52, 236, 'end', '8M'],
  [52, 250, 'end', '3M'],
  [60, 278, 'start', "Aug '24"],
  [222.5, 278, 'middle', "Feb '25"],
  [385, 278, 'middle', "Aug '25"],
  [547.5, 278, 'middle', "Feb '26"],
  [710, 278, 'end', "Aug '26"],
]

/**
 * The evidence chart's geometry, from `clients/marketing-pages/index.html:96-129` @ 654a67e447 copied EXACTLY —
 * every path `d`, coordinate and label is sourced usage data, not decoration, so nothing here is
 * rounded (overriding the `rendering-svg-precision` best practice for this one file).
 *
 * Hoisted (`rendering-hoist-jsx`): nothing below depends on props or state. The draw-in is keyed off
 * `data-visible` on the ref'd `<svg>` itself, read through the `[[data-visible]_&]:` descendant
 * variant, so the reveal can toggle one attribute on one element instead of nine. `reveal:` gates the
 * whole animation exactly as `reveal.tsx` does: without it every stroke and label renders fully drawn
 * and opaque from the first paint, so nothing is ever withheld from a visitor with JS off or reduced
 * motion on.
 *
 * Timings and colours were read as resolved computed styles off https://www.sonicjobs.com/, in both
 * themes, 2026-09-23 — the repo's own `styles.css` differs from what is deployed (a known drift noted
 * in `theme.css`). The draw is `stroke-dashoffset` 900px → 0 over 3000ms `ease`; dots and labels
 * fade in over 160ms `ease` starting 2840ms in, 160ms before the draw finishes.
 *
 * Colour: ChatGPT is the brand blue in light and white in dark, the same `accent`→`ink` pair
 * `<HeroHalo>` and `<PlatformRotator>` use. Gemini and Claude are a black/white-alpha pair (0.56/0.55
 * and 0.28/0.26) that does not land on a named ink token — a data-viz palette, not the UI ink
 * scale — so, like `employer-strip.tsx`'s logo filters, they carry their own measured light/dark
 * value instead of forcing a semantic token that would not match production. The axis line and every
 * other chart label land on `border-strong`/`ink-muted` exactly, so those stay tokens.
 */
const CHART_MARKS = (
  <>
    <line x1={60} y1={252} x2={710} y2={252} strokeWidth={1} className="stroke-border-strong" />
    <path
      d="M60,246.2 L141.25,244.3 L222.5,242.4 L303.75,239.5 L385,236.6 L466.25,233.7 L547.5,230.8 L628.75,226.9 L710,223.1"
      fill="none"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`stroke-[rgba(13,13,13,0.28)] dark:stroke-[rgba(255,255,255,0.26)] ${DRAW}`}
    />
    <path
      d="M60,236.6 L141.25,233.7 L222.5,228.9 L303.75,223.1 L385,217.3 L466.25,209.6 L547.5,201.9 L628.75,194.2 L710,184.5"
      fill="none"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`stroke-[rgba(13,13,13,0.56)] dark:stroke-[rgba(255,255,255,0.55)] ${DRAW}`}
    />
    <path
      d="M60,194.2 L141.25,184.5 L222.5,171.1 L303.75,155.6 L385,136.4 L466.25,117.1 L547.5,94 L628.75,68.9 L710,40"
      fill="none"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`stroke-accent dark:stroke-ink ${DRAW}`}
    />
    <circle cx={710} cy={40} r={4.5} className={`fill-accent dark:fill-ink ${FADE}`} />
    <circle
      cx={710}
      cy={184.5}
      r={4}
      className={`fill-[rgba(13,13,13,0.56)] dark:fill-[rgba(255,255,255,0.55)] ${FADE}`}
    />
    <circle
      cx={710}
      cy={223.1}
      r={4}
      className={`fill-[rgba(13,13,13,0.28)] dark:fill-[rgba(255,255,255,0.26)] ${FADE}`}
    />
    {AXIS.map(([x, y, anchor, label]) => (
      <text key={label} x={x} y={y} textAnchor={anchor} className="text-[10px] fill-ink-muted max-[761px]:text-[18px]">
        {label}
      </text>
    ))}
    <text
      x={700}
      y={28}
      textAnchor="end"
      className={`text-[11px] font-medium fill-accent dark:fill-ink ${FADE} max-[761px]:text-[20px]`}
    >
      ChatGPT · 110M weekly
    </text>
    <text
      x={700}
      y={174}
      textAnchor="end"
      className={`text-[11px] font-medium fill-ink-muted ${FADE} max-[761px]:text-[20px]`}
    >
      Gemini · 35M
    </text>
    <text
      x={700}
      y={214}
      textAnchor="end"
      className={`text-[11px] font-medium fill-ink-muted ${FADE} max-[761px]:text-[20px]`}
    >
      Claude · 15M
    </text>
  </>
)

/**
 * "The shift"'s evidence chart (`.growth`, `index.html:99-125`): weekly US users of the big three,
 * last two years. `useRevealOnView` watches the `<svg>` itself — production's own observer targets
 * `#gchart`, not the outer block — at the same `threshold`/`rootMargin` as `setupViewportMotion`
 * (`app.js:843-871`). Production redraws the chart on every re-entry (it removes and re-adds the class
 * that drives the animation, double-`requestAnimationFrame`d, whenever the chart scrolls back into
 * view); `useRevealOnView` reveals once and disconnects, so this draws once. Accepted delta, see
 * the deltas doc (docs/superpowers/specs/2026-09-22-s20-825-marketing-design-system-deltas.md), section 3.6.
 */
export default function GrowthChart(): JSX.Element {
  const { ref, visible } = useRevealOnView<SVGSVGElement>({ threshold: 0.2, rootMargin: '-8% 0px -8% 0px' })

  return (
    <div className="mt-[76px] border-t border-border-strong pt-[34px] max-[761px]:mt-[42px] max-[761px]:pt-[24px]">
      <div className="flex items-start justify-between gap-[40px] max-[761px]:flex-col max-[761px]:gap-[18px]">
        <div>
          <div className="text-[16px] font-semibold text-ink-secondary">The big three, last two years</div>
          <div className="mt-[4px] text-xs text-ink-secondary">Weekly US users, in millions</div>
        </div>
        <div className="flex flex-wrap justify-end gap-[18px] max-[761px]:justify-start">
          <span className="inline-flex items-center gap-[7px] text-xs font-medium text-ink-secondary">
            <i aria-hidden="true" className="inline-block h-[3px] w-[12px] bg-accent" />
            ChatGPT
          </span>
          <span className="inline-flex items-center gap-[7px] text-xs font-medium text-ink-secondary">
            <i aria-hidden="true" className="inline-block h-[3px] w-[12px] bg-ink-secondary" />
            Gemini
          </span>
          <span className="inline-flex items-center gap-[7px] text-xs font-medium text-ink-secondary">
            <i aria-hidden="true" className="inline-block h-[3px] w-[12px] bg-fill-strong" />
            Claude
          </span>
        </div>
      </div>
      <svg
        ref={ref}
        data-visible={visible || undefined}
        viewBox="0 0 720 292"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Weekly US users of ChatGPT, Gemini and Claude, 2024 to 2026"
        className="mt-[28px] block w-full overflow-visible"
      >
        {CHART_MARKS}
      </svg>
      <div className="mt-[16px] text-xs leading-normal text-ink-secondary">
        Source: OpenAI Global Affairs, platform announcements and third-party usage estimates, 2024 to 2026. US weekly
        actives, directional.
      </div>
    </div>
  )
}
